/**
 * MfaRecoveryCodesPage — the full-screen page that shows the one-time recovery
 * codes immediately after enrollment.
 *
 * Enrollment KEEPS the session alive, so the user is still signed in when this
 * renders and acknowledges their codes over that same session. It nonetheless
 * lives OUTSIDE `ProtectedRoute` on purpose: an involuntary session-expiry
 * redirect can then never unmount it (the failure mode that would destroy the
 * irrecoverable codes), and — being a bare auth-style page with no app nav
 * chrome — there are no in-app links to accidentally click away through. The
 * codes arrive via the in-memory `mfa-codes-handoff` (never storage or router
 * state).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  peekRecoveryCodes,
  peekRecoveryCodesGeneratedAt,
  clearRecoveryCodes,
} from '@/lib/mfa-codes-handoff';
// auth-api's untraced client on purpose: MFA calls must never enter the traced
// api.ts telemetry buffer (see MfaSettingsCard's header note).
import { authApi } from '@/lib/auth-api';
import { setAuthFlash } from '@/lib/auth-flash';
import { useAuth } from '@/auth/hooks/useAuth';
import { RecoveryCodesDisplay } from '@/auth/components/RecoveryCodesDisplay';
import { Button } from '@/components/ui/button';

function Shell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex flex-col bg-linear-to-b from-background to-muted/30">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/fias-logo.png" alt="Fias" className="h-16 w-auto mx-auto mb-4 dark:invert" />
            <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          </div>
          <div className="bg-card rounded-xl border shadow-sm p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function MfaRecoveryCodesPage() {
  const navigate = useNavigate();
  // Peek once on mount; the codes live only in this component's state after.
  const { user } = useAuth();
  const [codes] = useState(() => peekRecoveryCodes());
  // Captured before the mount effect clears the hand-off.
  const [generatedAt] = useState(() => peekRecoveryCodesGeneratedAt());

  // Take ownership immediately: clear the module hand-off once this page holds
  // the codes in state, so navigating away (Back) — or a different account
  // signing in within the tab — can't re-expose them by revisiting this public
  // route. Peek-in-initializer stays idempotent under StrictMode's
  // double-invoked initializer; the clear runs here, after the codes are safely
  // captured (and state survives StrictMode's simulated remount).
  useEffect(() => {
    clearRecoveryCodes();
  }, []);

  // Landed here without staged codes (a refresh or a direct visit). The codes
  // are gone and can't be re-shown — regenerating is the only way to get a set.
  if (!codes || codes.length === 0) {
    return (
      <Shell
        title="Recovery codes unavailable"
        subtitle="Your recovery codes have already been shown and can’t be displayed again."
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            If you didn’t save them, regenerate a fresh set from your account settings.
          </p>
          <Button asChild>
            <Link to="/auth/settings">Go to settings</Link>
          </Button>
        </div>
      </Shell>
    );
  }

  const onContinue = () => {
    // Enrollment keeps the session alive, so the acknowledgement completes here
    // and now over that live session — the user is never asked to confirm twice.
    //
    // A direct call rather than useMutation: this page is a standalone public
    // route by design, and nothing in the UI reads pending/error state, so
    // requiring a QueryClientProvider would add a dependency for no benefit.
    // Fire-and-forget — a failed ack must not trap the user on a page holding
    // irrecoverable codes; the settings card still surfaces the unacknowledged
    // state if it does fail.
    void authApi.mfaAcknowledgeRecoveryCodes().catch(() => {
      /* non-fatal: settings still shows the unacknowledged state */
    });
    clearRecoveryCodes();
    setAuthFlash('Two-factor authentication is on. Your recovery codes are saved.');
    navigate('/auth/settings', { replace: true });
  };

  return (
    <Shell
      title="Save your recovery codes"
      subtitle="Two-factor authentication is on. Store these before you continue."
    >
      <RecoveryCodesDisplay
        codes={codes}
        account={user?.email}
        generatedAt={generatedAt}
        renderAction={(confirmed) => (
          <Button className="w-full" disabled={!confirmed} onClick={onContinue}>
            Continue
          </Button>
        )}
      />
    </Shell>
  );
}
